/**
 * setupCardModel — pure derivation for the Chat tab's setup card: the
 * pre-draft exchange (pick a document type, pick a library, the next-step
 * hint) shown as ONE compact card instead of three to five bubbles.
 *
 * The exchange reads, message by message:
 *
 *   writer   "Selected Periodic Safety Update / Periodic Benefit-Risk Evaluation."
 *   Asthra   "Got it — Periodic Safety Update / Periodic Benefit-Risk Evaluation."
 *   Asthra   (older chats) "Next, type /library to pick … a new one. …"
 *   writer   'Selected library "Tafamidis PSUR".'
 *   Asthra   'Selected "Tafamidis PSUR".'
 *
 * Live rows carry the structured ``doctype_selected`` / ``library_selected``
 * / ``agent_note`` entry, so they are recognised by their kind. Persisted
 * history keeps only role + text (the chat store has no field for a message
 * kind), so those rows are recognised by the exact templates that wrote
 * them: the formatters in ``messageProjection``, plus the retired slash-command
 * hints (``LEGACY_HINTS``) that chats from before 2026-10-23 still hold.
 *
 * A writer's pick line folds only when Asthra's matching ack follows it, so
 * a writer who happens to type "Selected …" keeps their message.
 *
 * Pure: no React, no styling, no I/O.
 */

import { DOC_CATALOG, type DocCatalogEntry, isPrivateTemplateCode } from "./doc-catalog";
import type { EventLogEntry } from "./eventLog";
import {
  type ChatMessage,
  formatDoctypeAck,
  formatDoctypePick,
  formatLibraryAck,
  formatLibraryPick,
} from "./messageProjection";

// ---------------------------------------------------------------------------
// Detection
// ---------------------------------------------------------------------------

/** One setup choice: the document type or the library. */
export type SetupStep =
  | { kind: "doctype"; label: string; code: string | null }
  | { kind: "library"; name: string; created: boolean };

/** The command a setup hint points at. */
export type SetupCommand = "library" | "draft";

/**
 * What a chat row is to the setup card.
 *
 *   * ``pick`` — the writer's line for a choice (folds only with its ack).
 *   * ``ack``  — Asthra's acknowledgement of a choice.
 *   * ``both`` — a live entry that carries the pick and the ack together.
 *   * ``hint`` — Asthra's old "Next, type /library…" / "type /draft…" pointer
 *     (slash commands were removed 2026-10-23; only persisted history has it).
 */
export type SetupProbe = { part: "pick" | "ack" | "both"; step: SetupStep } | { part: "hint"; command: SetupCommand };

/** The value ``format`` was called with to produce ``text``, or null when
 *  ``text`` is not that template. Reads the template off the formatter
 *  itself, so detection can never drift from the copy. */
export function templateValue(text: string, format: (value: string) => string): string | null {
  const marker = "\u0000";
  const template = format(marker);
  const cut = template.indexOf(marker);
  if (cut < 0) return text === template ? "" : null;
  const prefix = template.slice(0, cut);
  const suffix = template.slice(cut + marker.length);
  if (text.length <= prefix.length + suffix.length) return null;
  if (!text.startsWith(prefix) || !text.endsWith(suffix)) return null;
  return text.slice(prefix.length, text.length - suffix.length);
}

/** The catalog's short code for a doctype display name ("PSUR/PBRER" for
 *  "Periodic Safety Update / …"), or null when no entry has that name. */
export function codeForDocName(name: string): string | null {
  const visit = (entries: readonly DocCatalogEntry[]): string | null => {
    for (const entry of entries) {
      if (entry.name === name) return entry.code;
      const child = entry.children ? visit(entry.children) : null;
      if (child) return child;
    }
    return null;
  };
  for (const domain of ["pharma", "device", "shared"] as const) {
    for (const phase of DOC_CATALOG[domain]) {
      const code = visit(phase.docs);
      if (code) return code;
    }
  }
  return null;
}

/** A doctype code worth showing as a chip: catalog codes, not the
 *  ``WS_*`` ids of private workspace templates. */
function displayCode(code: string | null | undefined): string | null {
  return code && !isPrivateTemplateCode(code) ? code : null;
}

/** The next-step hints Asthra posted while slash commands existed. Kept
 *  verbatim so those rows in older chats still fold into the card. */
const LEGACY_HINTS: Readonly<Record<string, SetupCommand>> = {
  "Next, type /library to pick an existing library or create a new one. Once it's set, you can /draft.": "library",
  "You're set up — type /draft when you're ready and I'll start the run.": "draft",
};

function hintCommand(text: string): SetupCommand | null {
  return LEGACY_HINTS[text] ?? null;
}

function libraryFrom(
  text: string,
  format: (name: string, created: boolean) => string,
): { name: string; created: boolean } | null {
  for (const created of [false, true]) {
    const name = templateValue(text, (v) => format(v, created));
    if (name) return { name, created };
  }
  return null;
}

/** How a persisted chat message reads to the setup card (by template). */
export function setupProbeForMessage(message: ChatMessage): SetupProbe | null {
  if (message.role === "user") {
    // Library first: 'Selected library "X".' also fits "Selected <label>.".
    const library = libraryFrom(message.text, formatLibraryPick);
    if (library) return { part: "pick", step: { kind: "library", ...library } };
    const label = templateValue(message.text, formatDoctypePick);
    if (label) return { part: "pick", step: { kind: "doctype", label, code: codeForDocName(label) } };
    return null;
  }
  if (message.role !== "assistant") return null;
  // Only plain notes: a routed reply, proposal or error is never setup.
  if (message.intent || message.insertTarget || message.editDecision || message.isError) return null;
  const label = templateValue(message.text, formatDoctypeAck);
  if (label) return { part: "ack", step: { kind: "doctype", label, code: codeForDocName(label) } };
  const library = libraryFrom(message.text, formatLibraryAck);
  if (library) return { part: "ack", step: { kind: "library", ...library } };
  const command = hintCommand(message.text);
  return command ? { part: "hint", command } : null;
}

/** How a live EventLog entry reads to the setup card (by its kind). */
export function setupProbeForEntry(entry: EventLogEntry): SetupProbe | null {
  const p = entry.payload;
  if (p.kind === "doctype_selected") {
    return {
      part: "both",
      step: { kind: "doctype", label: p.label, code: displayCode(p.docCode) ?? codeForDocName(p.label) },
    };
  }
  if (p.kind === "library_selected") {
    return { part: "both", step: { kind: "library", name: p.libraryName, created: !!p.created } };
  }
  if (p.kind === "agent_note") {
    const command = hintCommand(p.text);
    return command ? { part: "hint", command } : null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Folding
// ---------------------------------------------------------------------------

/** The setup card as the chat shows it. */
export interface SetupCard {
  key: string;
  /** Where the card sits in the chat: its first step. */
  at: number;
  /** Its newest step. */
  updatedAt: number;
  doctype: { label: string; code: string | null } | null;
  /** The writer switched to a different document type here. */
  doctypeChanged: boolean;
  library: { name: string; created: boolean } | null;
  /** The writer switched to a different library here. */
  libraryChanged: boolean;
  /** The command to offer next, or null once a run has started. */
  next: SetupCommand | null;
}

function sameStep(a: SetupStep, b: SetupStep): boolean {
  if (a.kind === "doctype" && b.kind === "doctype") return a.label === b.label;
  if (a.kind === "library" && b.kind === "library") return a.name === b.name && a.created === b.created;
  return false;
}

function applyStep(card: SetupCard, step: SetupStep, at: number): void {
  card.updatedAt = at;
  if (step.kind === "doctype") {
    if (card.doctype && card.doctype.label !== step.label) {
      card.doctypeChanged = true;
      // A library is scoped to its document type: switching the type
      // clears it (the shell does the same).
      card.library = null;
      card.libraryChanged = false;
    }
    card.doctype = { label: step.label, code: step.code ?? card.doctype?.code ?? null };
    return;
  }
  if (card.library && card.library.name !== step.name) card.libraryChanged = true;
  card.library = { name: step.name, created: step.created };
}

/** What the card offers next: the library while it is missing, else /draft. */
export function nextSetupCommand(card: Pick<SetupCard, "doctype" | "library">): SetupCommand {
  return card.doctype && !card.library ? "library" : "draft";
}

/**
 * Split a chat stream into the rows that stay in chat and the setup cards
 * that replace the rest. All setup rows between two drafting runs form one
 * card at the first one's place, updated in place by later choices; a run
 * starting closes the card (and drops its next-step action). ``runStarts``
 * are the stream's batch start times.
 *
 * Hints fold only into an open card. A writer's pick line folds only when
 * the next row is Asthra's ack of the same choice; an ack folds on its own.
 */
export function foldSetupRows<T extends { at: number }>(
  items: readonly T[],
  probe: (item: T) => SetupProbe | null,
  runStarts: readonly number[] = [],
): { kept: T[]; cards: SetupCard[] } {
  const ordered = items
    .map((item, i) => ({ item, i, p: probe(item) }))
    .sort((a, b) => a.item.at - b.item.at || a.i - b.i);
  const kept: T[] = [];
  const cards: SetupCard[] = [];
  // Held in an object so TypeScript does not narrow it across the closures.
  const cur: { open: SetupCard | null } = { open: null };
  const ranSince = (t: number, until = Number.POSITIVE_INFINITY) => runStarts.some((s) => s > t && s <= until);
  const close = () => {
    if (cur.open && ranSince(cur.open.updatedAt)) cur.open.next = null;
    cur.open = null;
  };
  const absorb = (step: SetupStep | null, at: number) => {
    if (cur.open && ranSince(cur.open.updatedAt, at)) close();
    let card = cur.open;
    if (!card) {
      card = {
        key: `setup-${at}`,
        at,
        updatedAt: at,
        doctype: null,
        doctypeChanged: false,
        library: null,
        libraryChanged: false,
        next: null,
      };
      cards.push(card);
      cur.open = card;
    }
    if (step) applyStep(card, step, at);
    else card.updatedAt = at;
    card.next = nextSetupCommand(card);
  };
  for (let k = 0; k < ordered.length; k++) {
    const { item, p } = ordered[k];
    if (!p) {
      kept.push(item);
    } else if (p.part === "hint") {
      // A hint belongs to the card it follows; on its own it stays a message.
      if (cur.open && !ranSince(cur.open.updatedAt, item.at)) absorb(null, item.at);
      else kept.push(item);
    } else if (p.part === "pick") {
      const ack = ordered[k + 1];
      if (ack?.p && ack.p.part === "ack" && sameStep(ack.p.step, p.step)) {
        absorb(p.step, item.at);
        absorb(null, ack.item.at);
        k++;
      } else {
        kept.push(item);
      }
    } else {
      absorb(p.step, item.at);
    }
  }
  close();
  return { kept, cards };
}
